import { usePermissions } from '@customerio/hydra-permissions/react';
import type { Tag } from '@customerio/hydra-ui-api';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { useObjectTypes } from '@/services/db/object-type/queries';
import { useDataIndexItem, useItemReach, useItemTags, useWorkspaceTagList } from '../item/hooks';
import type { DataIndexItemDetail, ItemReach } from '../item/hooks';
import { useItemWrites, type ItemWrites } from '../item/item-writes';
import { useItemPageModel, type ItemPageModel } from '../item/page-model';
import { isEditableInDrawer } from '../utils/editing';
import { DataIndexLocation } from '../utils/types';
import type { DataIndexItemAddress } from '../urls';
import { useDrawerEditing, type DrawerEditing } from './use-drawer-editing';

/**
 * Everything the drawer reads and writes about one row.
 *
 * The same reads the item page makes, so opening a row and then opening its
 * page costs one request between them — and the two never disagree about
 * what the row says or what may be done to it.
 */

export type DrawerItem = {
  item: DataIndexItemDetail;
  /** The tags the row carries, named. */
  tags: Tag[];
  /** Every tag the workspace has, for the picker. */
  allTags: Tag[];
  reach: ItemReach;
  model: ItemPageModel;
  writes: ItemWrites;
  editing: DrawerEditing;
  /** Whether this viewer may change the row's description and tags. */
  canEdit: boolean;
  /** Whether this viewer may see whether the attribute is redacted. */
  canSeeSensitivity: boolean;
};

type DrawerItemInput = {
  workspaceId: number;
  address: DataIndexItemAddress;
  /** Called once the attribute is gone: the drawer closes and the list forgets it. */
  onDeleted: () => void;
};

export function useDrawerItem({ workspaceId, address, onDeleted }: DrawerItemInput): DrawerItem {
  const item = useDataIndexItem(workspaceId, address);
  const tags = useItemTags(workspaceId, item.tagIds);
  const allTags = useWorkspaceTagList(workspaceId);
  const environment = useEnvironmentOptional();
  const objectTypes = useObjectTypes().data;
  const { hasPermission } = usePermissions();

  const model = useItemPageModel({
    address,
    item,
    objectTypes,
    identifiers: environment.data?.environment?.identifiers,
    isEnvironmentLoading: environment.data == null,
    canDelete: hasPermission('journeys/people/edit'),
  });
  const reach = useItemReach(workspaceId, address, model.reach.withRow);
  const description = model.description ?? '';
  const writes = useItemWrites({
    workspaceId,
    kind: address.kind,
    location: DataIndexLocation.Drawer,
    scope: address.scope,
    name: item.name,
    description,
    tags,
    privacyLevel: item.privacyLevel,
    onDeleted,
  });
  const editing = useDrawerEditing({ item, description, writes });

  const canEditMetadata = hasPermission('journeys/attribute_metadata/edit');

  return {
    item,
    tags,
    allTags,
    reach,
    model,
    writes,
    editing,
    canEdit: canEditMetadata && isEditableInDrawer(address.kind, item.name),
    canSeeSensitivity: address.kind === 'attribute' && canEditMetadata,
  };
}
